import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import { AssetStatusType } from '@design-stack-vista/vista-assets-sdk';
import { css } from '@emotion/css';
import { FluidImage } from '@vp/swan';
import { observer } from 'mobx-react-lite';
import { useEffect, useRef, useState } from 'react';
import { useDrawerData } from '../../DrawerProvider';
import { useDrawerThumbnailAsset } from './DrawerThumbnailAssetProvider';
import { DrawerThumbailSpinner } from './DrawerThumbnailSpinner';
import { useLocalization } from '../../../../Localization';
const imageStyle = css `
    &.swan-fluid-image {
        object-fit: contain;
        height: 100%;
        width: 100%;
        margin: auto;
        border-radius: calc(var(--swan-sem-border-radius-subtle) - 1px);
    }
`;
const getThumbnail = (asset, pageNum) => {
    const options = { pageNum };
    try {
        return asset.thumbnail.getUrl(options);
    }
    catch (_a) {
        try {
            return asset.getUrl(options);
        }
        catch (_b) {
            return;
        }
    }
};
export const DrawerThumbnailFluidImage = observer(() => {
    var _a, _b, _c, _d;
    const { asset, asset: { data, status, isPresigned }, pageNumber, setShowProcessing, onThumbnailAssetError, isError, } = useDrawerThumbnailAsset();
    const { loadingLabel } = useLocalization();
    const fileName = (_b = (_a = data === null || data === void 0 ? void 0 : data.info) === null || _a === void 0 ? void 0 : _a.storage) === null || _b === void 0 ? void 0 : _b.fileName;
    const timeoutId = useRef(undefined);
    const [loading, setLoading] = useState(true);
    const [thumbnailUrl, setThumbnailUrl] = useState();
    useEffect(() => {
        // Set the initial loading state to false.
        // Then in 150ms set it to true.
        // This will allow newly uploaded images to show the indicator
        // The loading will go away when the upload is complete
        timeoutId.current = setTimeout(() => {
            setLoading(true);
        }, 150);
        return () => {
            clearTimeout(timeoutId.current);
        };
    }, []);
    const { onImageLoadStart, onImageLoadCompleted, onImageLoadError } = useDrawerData();
    useEffect(() => {
        if (asset.status.type === AssetStatusType.Prepared) {
            onImageLoadStart === null || onImageLoadStart === void 0 ? void 0 : onImageLoadStart(asset);
        }
    }, [asset, onImageLoadStart]);
    useEffect(() => {
        var _a, _b;
        // if the asset is not prepared or presigned retrieving the thumbnail url will fail
        // if the asset info is not available we don't know how many pages it is.  Trying to retrieve the thumbnail while specifying a page will fail
        if (status.type !== AssetStatusType.Prepared || !isPresigned || !((_b = (_a = asset.data) === null || _a === void 0 ? void 0 : _a.info) === null || _b === void 0 ? void 0 : _b.image) || isError) {
            return;
        }
        const src = getThumbnail(asset, pageNumber);
        if (!src) {
            onThumbnailAssetError();
        }
        else {
            setThumbnailUrl(src);
        }
        return;
    }, [asset, isError, isPresigned, onThumbnailAssetError, pageNumber, status.type, (_d = (_c = asset.data) === null || _c === void 0 ? void 0 : _c.info) === null || _d === void 0 ? void 0 : _d.image]);
    if (!thumbnailUrl) {
        return null;
    }
    return (_jsxs(_Fragment, { children: [loading && _jsx(DrawerThumbailSpinner, { showPercent: false, title: loadingLabel }), _jsx(FluidImage, { className: imageStyle, src: thumbnailUrl, onLoadStart: () => {
                    setShowProcessing(false);
                }, onLoad: () => {
                    setLoading(false);
                    onImageLoadCompleted === null || onImageLoadCompleted === void 0 ? void 0 : onImageLoadCompleted(asset);
                    setShowProcessing(false);
                    clearTimeout(timeoutId.current);
                }, onError: () => {
                    onImageLoadError === null || onImageLoadError === void 0 ? void 0 : onImageLoadError(asset);
                    setLoading(false);
                    setShowProcessing(false);
                    clearTimeout(timeoutId.current);
                    // we don't handle this error currently
                    // we can't hide the asset in case the error was transient
                    // we can't hide the thumbnail because other parts of the UX depend on the existence of this thumbnail to render themselves
                    // handling this error state is tracked in https://vistaprint.atlassian.net/browse/DT-13658
                }, alt: fileName })] }));
});
DrawerThumbnailFluidImage.displayName = 'DrawerThumbnailFluidImage';
//# sourceMappingURL=DrawerThumbnailFluidImage.js.map